import { useState, useEffect, useRef } from 'react';
import { X, Search, Check, EyeOff } from 'lucide-react';
import {
  searchBookingsForMatch, setPostingMatch, ignorePosting, getOpenItems,
  type PaymentPosting, type BookingSearchHit, type OpenItem,
} from '../api';

const MONTHS_DK = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];
const fmtDate = (iso: string) => {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${parseInt(m[3])}. ${MONTHS_DK[parseInt(m[2])-1]} ${m[1]}`;
};
const fmtAmount = (n: number) => n.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface Props {
  posting: PaymentPosting;
  onClose: () => void;
  onSaved: () => void;
}

export default function MatchPostingModal({ posting, onClose, onSaved }: Props) {
  const [query, setQuery]   = useState('');
  const [hits, setHits]     = useState<BookingSearchHit[]>([]);
  const [openItems, setOpenItems] = useState<OpenItem[]>([]);
  const [loadingOpen, setLoadingOpen] = useState(true);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Åbne poster hentes altid. Et ukendt beløb hører næsten altid til noget der stadig mangler
  // at blive betalt, og så er det nemmere at vælge derfra end at gætte hvilken booking det var.
  // Beløbet sendes med, så serveren kan lægge de poster øverst hvor udestående passer.
  useEffect(() => {
    getOpenItems(Number(posting.amount))
      .then(setOpenItems)
      .catch(() => setOpenItems([]))
      .finally(() => setLoadingOpen(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posting.id]);

  const runSearch = async (q: string) => {
    if (q.trim().length < 1) { setHits([]); return; }
    setSearching(true);
    try {
      const results = await searchBookingsForMatch(q);
      setHits(results);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Søge-fejl');
    } finally {
      setSearching(false);
    }
  };

  const handleQueryChange = (v: string) => {
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(v), 250);
  };

  const matchTo = async (bookingId: number | null) => {
    setSaving(true);
    setError(null);
    try {
      await setPostingMatch(posting.id, bookingId);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kunne ikke gemme');
      setSaving(false);
    }
  };

  const handleIgnore = async () => {
    setSaving(true);
    setError(null);
    try {
      await ignorePosting(posting.id);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kunne ikke ignorere');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-cream-50 rounded-xl shadow-card-md w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-ink-100 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-ink-900">Match postering til booking</div>
            <div className="text-xs text-ink-500 mt-0.5">
              {fmtDate(posting.posting_date)} · {posting.description} · {posting.renter_name || '—'} · {posting.registration_number || '—'} · <strong>{fmtAmount(posting.amount)} DKK</strong>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-cream-100 text-ink-500">
            <X size={18} />
          </button>
        </div>

        {/* Søgefelt */}
        <div className="px-5 py-3 border-b border-ink-100">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300" />
            <input type="text" value={query} onChange={e => handleQueryChange(e.target.value)} autoFocus
              placeholder="Søg på lejer, nummerplade, UID eller faktura-nr…"
              className="filter-input pl-9 w-full" />
          </div>
        </div>

        {/* Resultatliste. Uden søgning vises de åbne poster — det er dem et ukendt beløb
            næsten altid hører til, og de er nemmere at genkende end en fri søgning. */}
        <div className="flex-1 overflow-y-auto">
          {searching && <div className="p-4 text-center text-ink-500 text-sm">Søger…</div>}
          {!searching && hits.length === 0 && query.length > 0 && (
            <div className="p-4 text-center text-ink-500 text-sm">Ingen bookings matcher "{query}"</div>
          )}
          {!searching && query.length === 0 && (
            loadingOpen ? (
              <div className="p-4 text-center text-ink-500 text-sm">Henter åbne poster…</div>
            ) : openItems.length === 0 ? (
              <div className="p-4 text-center text-ink-400 text-sm">
                Ingen bookings mangler penge. Søg for at finde en booking alligevel.
              </div>
            ) : (
              <>
                <div className="px-5 py-2 text-xs font-semibold text-ink-500 bg-cream-100 border-b border-ink-100">
                  Åbne poster ({openItems.length}) — bookings der stadig mangler penge
                </div>
                <ul className="divide-y divide-ink-100">
                  {openItems.map(b => {
                    const isCurrentMatch = posting.booking_id === b.id;
                    return (
                      <li key={b.id} className={`px-5 py-3 hover:bg-sage-50 ${b.suggested ? 'bg-[var(--color-warning-100)]' : ''} ${isCurrentMatch ? 'bg-sage-100' : ''}`}>
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-ink-900">
                              {b.renter_name} <span className="text-ink-500">·</span> {b.registration_number}
                              {b.suggested && (
                                <span className="ml-2 text-[10px] uppercase tracking-wide font-semibold text-amber-700">
                                  Beløbet passer
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-ink-500 mt-0.5">
                              {fmtDate(b.s)}{b.s !== b.e && ` → ${fmtDate(b.e)}`} · UID {b.uid}
                              {b.invoice_number && <span> · {b.invoice_number}</span>}
                            </div>
                            <div className="text-xs mt-0.5 text-ink-700">
                              Mangler <strong className={b.outstanding > 0 ? 'text-rose-700' : 'text-ink-700'}>
                                {fmtAmount(b.outstanding)} DKK
                              </strong>
                              <span className="text-ink-400"> · forventet {fmtAmount(b.expected)}, modtaget {fmtAmount(b.received)}</span>
                            </div>
                          </div>
                          <button type="button" disabled={saving || isCurrentMatch} onClick={() => matchTo(b.id)}
                            className="flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded bg-sage-600 text-white hover:bg-sage-700 disabled:bg-ink-200">
                            {isCurrentMatch ? <><Check size={12} className="inline mr-1" />Aktuel</> : 'Vælg'}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </>
            )
          )}
          {hits.length > 0 && (
            <ul className="divide-y divide-ink-100">
              {hits.map(b => {
                const isCurrentMatch = posting.booking_id === b.id;
                return (
                  <li key={b.id} className={`px-5 py-3 hover:bg-sage-50 ${isCurrentMatch ? 'bg-sage-100' : ''}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-ink-900">
                          {b.renter_name} <span className="text-ink-500">·</span> {b.registration_number}
                        </div>
                        <div className="text-xs text-ink-500 mt-0.5">
                          {fmtDate(b.s)}{b.s !== b.e && ` → ${fmtDate(b.e)}`} · UID {b.uid}
                          {b.invoice_number && <span> · {b.invoice_number}</span>}
                        </div>
                        <div className="text-xs text-ink-700 mt-0.5">Faktura-payout: <strong>{fmtAmount(Number(b.payout))} DKK</strong></div>
                      </div>
                      <button type="button" disabled={saving || isCurrentMatch} onClick={() => matchTo(b.id)}
                        className="flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded bg-sage-600 text-white hover:bg-sage-700 disabled:bg-ink-200">
                        {isCurrentMatch ? <><Check size={12} className="inline mr-1" />Aktuel</> : 'Vælg'}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer: andre handlinger */}
        <div className="px-5 py-3 border-t border-ink-100 flex items-center justify-between bg-cream-100">
          <div className="text-xs text-ink-500">
            {posting.booking_id && <span>Aktuelt matched til booking #{posting.booking_id}</span>}
            {!posting.booking_id && posting.match_type === 'ignored' && <span>Aktuelt markeret som ignoreret</span>}
            {!posting.booking_id && posting.match_type !== 'ignored' && <span>Aktuelt ikke matched</span>}
          </div>
          <div className="flex gap-2">
            {posting.booking_id && (
              <button type="button" disabled={saving} onClick={() => matchTo(null)}
                className="px-3 py-1.5 text-xs rounded border border-ink-200 hover:bg-cream-50">
                Fjern match
              </button>
            )}
            <button type="button" disabled={saving} onClick={handleIgnore}
              className="px-3 py-1.5 text-xs rounded border border-ink-200 hover:bg-cream-50 inline-flex items-center gap-1">
              <EyeOff size={12} /> Markér som ignoreret
            </button>
          </div>
        </div>

        {error && (
          <div className="px-5 py-2 bg-rose-50 text-rose-700 text-sm border-t border-rose-200">{error}</div>
        )}
      </div>
    </div>
  );
}
